import moment from 'moment';

function hasText(value) {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function buildHeaderLines(requestType, header = {}) {
  const lines = ['Loyal Message History', requestType];

  lines.push(`Parent: ${header.parent || ''}`);
  lines.push(`Pet: ${header.pet || ''}`);

  const doctorLabel = hasText(header.doctor) ? String(header.doctor).trim() : '';
  const doctorName = doctorLabel.replace(/^Dr\.?\s*/i, '').trim();
  if (doctorName && doctorName.toUpperCase() !== 'N/A') {
    lines.push(`Doctor: ${doctorName}`);
  }

  if (requestType === 'Refill Request') {
    lines.push(`Medication: ${header.medication || ''}`);
    lines.push(`Quantity: ${header.quantity || ''}`);
    lines.push(`Dosage: ${header.dosage || ''}`);
  }

  if (requestType === 'Appointment Request') {
    lines.push(`Appointment type: ${header.appointmentType || ''}`);
  }

  if (requestType === 'Inquiry') {
    lines.push(`Topic: ${header.topic || ''}`);
  }

  lines.push(`Submitted: ${header.submitted || ''}`);
  lines.push(`Copied: ${moment().format('MM/DD/YYYY hh:mm A')}`);

  return lines;
}

function padLabel(label) {
  return String(label).padEnd(3, ' ');
}

const BODY_MAX = 68; // column-2 width; hang = 3-char label + 1 space
const HANG = '    ';

function wrapBody(text, width = BODY_MAX) {
  const paragraphs = String(text).split('\n');
  const out = [];

  paragraphs.forEach((para) => {
    const words = para.length ? para.split(/\s+/) : [''];
    let line = '';

    words.forEach((word) => {
      if (!line) {
        let rest = word;
        while (rest.length > width) {
          out.push(rest.slice(0, width));
          rest = rest.slice(width);
        }
        line = rest;
        return;
      }
      if (line.length + 1 + word.length <= width) {
        line += ` ${word}`;
        return;
      }
      out.push(line);
      let rest = word;
      while (rest.length > width) {
        out.push(rest.slice(0, width));
        rest = rest.slice(width);
      }
      line = rest;
    });

    out.push(line);
  });

  return out;
}

function formatLabeledLine(label, body) {
  const parts = wrapBody(body);
  if (!parts.length) return padLabel(label);
  const first = `${padLabel(label)} ${parts[0]}`;
  const rest = parts.slice(1).map((p) => `${HANG}${p}`);
  return [first, ...rest].join('\n');
}

/** Label + kind on one line; URL below, wrapped at BODY_MAX, no indent. */
function formatAttachmentLine(label, kind, url) {
  const width = BODY_MAX;
  const s = String(url);
  const parts = [];
  let rest = s;
  while (rest.length > width) {
    parts.push(rest.slice(0, width));
    rest = rest.slice(width);
  }
  parts.push(rest);
  return [`${label} ${kind}:`, ...parts].join('\n');
}

function attachmentLabel(msgNum, index, total) {
  if (total === 1) return String(msgNum);
  return `${msgNum}${String.fromCharCode(97 + index)}`; // 5a, 5b, …
}

/** PDFs first, then images. */
function collectMessageAttachments(message) {
  const out = [];
  const pdfs = Array.isArray(message.pdfs) ? message.pdfs : [];
  const images = Array.isArray(message.images) ? message.images : [];
  pdfs.forEach((url) => {
    if (hasText(url)) out.push({ kind: 'PDF', url: String(url).trim() });
  });
  images.forEach((url) => {
    if (hasText(url)) out.push({ kind: 'Image', url: String(url).trim() });
  });
  return out;
}

function buildMessageLine(message, msgNum) {
  const date = message.date || '';
  const time = message.time || '';
  const body = message.message || '';
  const isClient = message.authorType === 'client';
  const bodyText = isClient ? `* ${body}` : body;
  const hasAtt = collectMessageAttachments(message).length > 0;
  const main = `${date} ${time} ${bodyText}`.trim();
  const withMark = hasAtt ? `${main} [+]` : main;
  return formatLabeledLine(msgNum, withMark);
}

/**
 * Build plain-text Message History transcript for clipboard copy.
 * Clipboard order is chronological (opposite of Message History table).
 * Left column is 3 chars (flush left; covers labels like 25a), then body.
 */
export function buildMessageHistoryTranscript({
  requestType,
  header = {},
  messages = [],
}) {
  const headerLines = buildHeaderLines(requestType, header);
  // UI is newest-first; paste oldest → newest
  const chrono = [...(messages || [])].reverse();

  const messageLines = chrono.map((m, i) => buildMessageLine(m, i + 1));

  const attachmentLines = [];
  chrono.forEach((m, i) => {
    const msgNum = i + 1;
    const atts = collectMessageAttachments(m);
    atts.forEach((att, attIdx) => {
      const label = attachmentLabel(msgNum, attIdx, atts.length);
      attachmentLines.push(
        formatAttachmentLine(label, att.kind, att.url),
      );
    });
  });

  const parts = [
  headerLines.join('\n'),
  '',
  '* = client message',
  '[+] = message has attachment(s)',
  '',
  'MESSAGE HISTORY:',
  messageLines.join('\n\n'),
];

if (attachmentLines.length) {
  parts.push('', 'MESSAGE ATTACHMENTS:', attachmentLines.join('\n\n'));
}

  return parts.join('\n').trimEnd();
}
